import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ArrowButton from "../../components/ArrowButton/ArrowButton";
import "../../components/Forms/ContactForm.css";
import { EDUCATION_OPTIONS, NEXT_STEPS } from "./UniversidadeContent";

const SIGNUP_URL = "https://backend.calriz.com/api/universidade-calriz";

const EMPTY_FORM = { nome: "", email: "", telefone: "", formacao: "" };

const DEFAULT_ERROR = "Não foi possível enviar sua inscrição. Tente novamente.";

type Step = "form" | "codigo" | "success";

function maskPhone(raw: string) {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// O backend devolve { error } com a mensagem pronta para o usuário e, quando aplicável, reenvioEmSegundos
async function postSignup(path: string, payload: object) {
  const response = await fetch(`${SIGNUP_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, data };
}

// Inscrição com o mesmo visual do formulário de contato (caixa escura + card branco). Fica no fim da página e é o
// destino de todos os botões de inscrição (#inscricao). Depois do envio, o aluno confirma o e-mail com um código de
// 6 dígitos no próprio card.
export default function UniversidadeForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [accepted, setAccepted] = useState(false);
  // Honeypot: campo invisível para pessoas; se vier preenchido, o backend descarta a inscrição
  const [website, setWebsite] = useState("");
  const [step, setStep] = useState<Step>("form");
  const [codigo, setCodigo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === "telefone" ? maskPhone(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const digits = form.telefone.replace(/\D/g, "").length;
    if (digits !== 10 && digits !== 11) {
      setError("Informe um telefone válido com DDD.");
      return;
    }

    setError(null);
    setNotice(null);
    setIsSubmitting(true);

    try {
      const { ok, data } = await postSignup("", { ...form, aceiteTermos: accepted, website });
      if (ok) {
        setCodigo("");
        setCooldown(data.reenvioEmSegundos ?? 60);
        setStep("codigo");
      } else {
        setError(data.error ?? DEFAULT_ERROR);
      }
    } catch (err) {
      console.error("Erro ao enviar inscrição:", err);
      setError(DEFAULT_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (codigo.length !== 6) {
      setError("Informe o código de 6 dígitos.");
      return;
    }

    setError(null);
    setNotice(null);
    setIsSubmitting(true);

    try {
      const { ok, data } = await postSignup("/confirmar", { email: form.email, codigo });
      if (ok) {
        setStep("success");
        setForm(EMPTY_FORM);
        setAccepted(false);
        setCodigo("");
      } else {
        setError(data.error ?? "Não foi possível confirmar o código. Tente novamente.");
      }
    } catch (err) {
      console.error("Erro ao confirmar inscrição:", err);
      setError("Não foi possível confirmar o código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setNotice(null);
    setIsSubmitting(true);

    try {
      const { ok, data } = await postSignup("/reenviar", { email: form.email });
      if (data.reenvioEmSegundos) setCooldown(data.reenvioEmSegundos);
      if (ok) {
        setCodigo("");
        setNotice("Enviamos um novo código para o seu e-mail.");
      } else {
        setError(data.error ?? "Não foi possível reenviar o código. Tente novamente.");
      }
    } catch (err) {
      console.error("Erro ao reenviar código:", err);
      setError("Não foi possível reenviar o código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Volta para o formulário mantendo os dados preenchidos
  const handleChangeEmail = () => {
    setStep("form");
    setCodigo("");
    setError(null);
    setNotice(null);
  };

  return (
    <section className="cz-contact cz-uni-signup" id="inscricao" aria-labelledby="cz-uni-signup-title">
      <div className="cz-contact__box">
        <div className="cz-contact__intro">
          <span className="cz-eyebrow cz-eyebrow--on-dark">Inscrições abertas</span>
          <h2 className="cz-contact__title" id="cz-uni-signup-title">
            Pronto para se capacitar, obter sua certificação e iniciar sua carreira na Calriz?
          </h2>
          <p className="cz-contact__text">
            Garanta sua inscrição, acompanhe seu e-mail para a liberação do acesso e prepare-se para disputar um dos
            vouchers de certificação oficial.
          </p>
          <div className="cz-contact__spacer" />
          <ol className="cz-uni-next">
            {NEXT_STEPS.map((text, i) => (
              <li key={text}>
                <span className="cz-uni-next__num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                {text}
              </li>
            ))}
          </ol>
        </div>

        <div className="cz-contact__card">
          {step === "success" && (
            <div className="cz-contact__sent" role="status">
              <span className="cz-shape cz-shape--drop cz-contact__sent-shape" aria-hidden="true" />
              <strong>Inscrição confirmada!</strong>
              <span>
                Fique de olho no seu e-mail: em breve você recebe as orientações para criar sua conta na plataforma e
                começar os treinamentos.
              </span>
              <button type="button" className="cz-contact__again" onClick={() => setStep("form")}>
                Fazer outra inscrição
              </button>
            </div>
          )}

          {step === "codigo" && (
            <form className="cz-contact__form" onSubmit={handleConfirm}>
              <p className="cz-uni-code__text">
                Enviamos um código de 6 dígitos para <strong>{form.email}</strong>. Digite-o abaixo para confirmar sua
                inscrição.
              </p>

              <label className="cz-contact__field cz-contact__field--full">
                <span className="cz-contact__label">Código de confirmação*</span>
                <input
                  type="text"
                  name="codigo"
                  className="cz-uni-code__input"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  disabled={isSubmitting}
                />
              </label>

              <ArrowButton type="submit" variant="pink" size="lg" className="cz-contact__submit" disabled={isSubmitting}>
                {isSubmitting ? "Confirmando..." : "Confirmar inscrição"}
              </ArrowButton>

              <div className="cz-uni-code__actions">
                <button
                  type="button"
                  className="cz-contact__again"
                  onClick={handleResend}
                  disabled={isSubmitting || cooldown > 0}
                >
                  {cooldown > 0 ? `Reenviar código em ${cooldown}s` : "Reenviar código"}
                </button>
                <button type="button" className="cz-contact__again" onClick={handleChangeEmail} disabled={isSubmitting}>
                  Alterar e-mail
                </button>
              </div>

              {notice && (
                <p className="cz-uni-code__notice" role="status">
                  {notice}
                </p>
              )}
              {error && (
                <p className="cz-contact__error" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}

          {step === "form" && (
            <form className="cz-contact__form" onSubmit={handleSubmit}>
              <label className="cz-contact__field cz-contact__field--full">
                <span className="cz-contact__label">Nome completo*</span>
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="Nome e sobrenome"
                  autoComplete="name"
                  required
                  disabled={isSubmitting}
                />
              </label>

              <label className="cz-contact__field">
                <span className="cz-contact__label">E-mail*</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="voce@email.com"
                  autoComplete="email"
                  required
                  disabled={isSubmitting}
                />
              </label>

              <label className="cz-contact__field">
                <span className="cz-contact__label">Telefone (WhatsApp)*</span>
                <input
                  type="tel"
                  name="telefone"
                  value={form.telefone}
                  onChange={handleChange}
                  placeholder="(XX) XXXXX-XXXX"
                  autoComplete="tel"
                  required
                  disabled={isSubmitting}
                />
              </label>

              <label className="cz-contact__field cz-contact__field--full">
                <span className="cz-contact__label">Formação*</span>
                <select name="formacao" value={form.formacao} onChange={handleChange} required disabled={isSubmitting}>
                  <option value="">Selecione sua formação</option>
                  {EDUCATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>

              <label className="cz-uni-hp" aria-hidden="true">
                Não preencha este campo
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>

              <label className="cz-uni-consent">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  required
                  disabled={isSubmitting}
                />
                <span>
                  Li e aceito os <Link to="/termos-de-uso">Termos de Uso</Link>, o{" "}
                  <Link to="/universidade-calriz/regulamento" target="_blank" rel="noopener noreferrer">
                    Regulamento do Programa
                  </Link>{" "}
                  e a{" "}
                  <Link to="/politica-de-privacidade">Política de Privacidade</Link>.
                </span>
              </label>

              <ArrowButton type="submit" variant="pink" size="lg" className="cz-contact__submit" disabled={isSubmitting}>
                {isSubmitting ? "Enviando..." : "Garantir minha inscrição agora"}
              </ArrowButton>

              {error && (
                <p className="cz-contact__error" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
